import { useState } from "preact/hooks";
import type { FieldType } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { fieldCommentCount } from "../../lib/commentCounts.ts";
import { hasSettings, nextUid, type Row } from "../../lib/fieldRows.ts";
import { spaceUrl } from "../../lib/paths.ts";
import { MAX_FIELDS } from "../../lib/schemaLimits.ts";
import { useId } from "../../lib/uid.ts";
import { Button } from "../../ui/Button.tsx";
import { CommentCount } from "../../ui/counts.tsx";
import { Input, Select } from "../../ui/form.tsx";
import { ArrowDownIcon, ArrowUpIcon, CloseIcon } from "../../ui/icons.tsx";
import { TH } from "../../ui/styles.ts";
import { type Env, Flag, Settings, SettingsToggle, type Size } from "./fieldSettings.tsx";

const TYPES: readonly FieldType[] = [
  "text",
  "email",
  "phone",
  "url",
  "number",
  "percentage",
  "boolean",
  "date",
  "time",
  "datetime",
  "local_datetime",
  "instant",
  "duration",
  "timezone",
  "select",
  "money",
  "quantity",
  "location",
  "any",
  "ref",
];
/** The table's columns (key, label, type, the three flags, actions): what a settings row spans. */
const COLUMNS = 7;
/** Cell padding, and the height of the row's controls (flags center on it). */
const CELL = { normal: "px-2 py-1.5 align-top", compact: "px-2 py-1 align-top" };
const LINE = { normal: "h-9", compact: "h-7" };
/** Headings over the narrow flag columns: condensed, so the words don't widen the columns. */
const FLAG_TH = "px-2 py-2 text-center font-medium tracking-tight whitespace-nowrap text-zinc-600";

const blank = (): Row => ({ uid: nextUid(), key: "", type: "text" });

/** A field's settings toggle: whether its settings are open, and the id of what it shows and hides. */
interface Toggle {
  has: boolean;
  open: boolean;
  /** Open, or read-only (no toggles: everything shows). */
  shown: boolean;
  id: string;
  flip: () => void;
  reveal: () => void;
}

/** New fields, and choices without options yet, start with their settings open: they need filling in. */
function useToggle(r: Row, env: Env, link: boolean): Toggle {
  const [open, setOpen] = useState(() => !r.orig || (r.type === "select" && !r.options?.length));
  const id = useId();
  return {
    has: hasSettings(r.type, link),
    open,
    shown: open || env.readOnly,
    id,
    flip: () => setOpen(!open),
    reveal: () => setOpen(true),
  };
}

interface RowProps {
  row: Row;
  env: Env;
  /** The ref field this is a link field of: plain types only, shown in its section. */
  parent?: Row | undefined;
  onChange: (r: Row) => void;
  onRemove: () => void;
  onMove: (by: number) => void;
}

/** The key (monospace: an identifier for data and code), noting a rename. */
function KeyCell({
  r,
  parent,
  set,
  env,
}: {
  r: Row;
  parent: Row | undefined;
  set: (p: Partial<Row>) => void;
  env: Env;
}) {
  const link = parent !== undefined;
  return (
    <td class={CELL[link ? "compact" : "normal"]}>
      {/* A section's contents are indented under the field's row. */}
      <div class={link ? "min-w-32 pl-4" : "min-w-32"}>
        <Input
          compact={link}
          aria-label={link ? t("fields.linkKey", { field: parent.key }) : t("fields.key")}
          value={r.key}
          onValue={(v) => set({ key: v })}
          required={true}
          pattern="[A-Za-z_][A-Za-z0-9_]*"
          class="font-mono"
        />
        {r.orig && r.orig !== r.key ? (
          <span class="mt-1 block text-xs text-zinc-500">{t("fields.renamed", { from: r.orig })}</span>
        ) : null}
        {/* The field's comments (as saved: a renamed field keeps its conversation under its old key until saved). */}
        {!link && r.orig && env.space && env.saved ? (
          <span class="-ml-1.5 mt-1 block">
            <CommentCount
              count={fieldCommentCount(env.space.id, env.saved.id, r.orig)}
              href={spaceUrl(env.space, `/entities/${env.saved.id}?tab=conversation`)}
            />
          </span>
        ) : null}
      </div>
    </td>
  );
}

/** The type, and beside it the toggle for the type's settings (a gap for types without). */
function TypeCell(p: {
  r: Row;
  env: Env;
  link: boolean;
  name: string;
  toggle: Toggle;
  set: (p: Partial<Row>) => void;
}) {
  const size: Size = p.link ? "compact" : "normal";
  return (
    <td class={CELL[size]}>
      <div class="flex items-center gap-1">
        <Select
          aria-label={t("fields.type")}
          compact={p.link}
          class="min-w-32"
          value={p.r.type}
          onValue={(v) => {
            // A format, region or units belong to the type they were chosen for.
            p.set({ type: v as FieldType, format: null, region: null, strict: false, unit: null, units: [] });
            p.toggle.reveal(); // a new type's settings need filling in
          }}
          options={TYPES.filter((x) => !(p.link && x === "ref")).map((x) => ({
            value: x,
            label: t(`type.${x}`),
          }))}
        />
        {p.env.readOnly ? null : (
          <SettingsToggle
            size={size}
            has={p.toggle.has}
            open={p.toggle.open}
            controls={p.toggle.id}
            label={t("fields.settingsOf", { field: p.name })}
            onToggle={p.toggle.flip}
          />
        )}
      </div>
    </td>
  );
}

/** A flag (required, many), centered in its column. */
function FlagCell({ size, ...flag }: Parameters<typeof Flag>[0] & { size: Size }) {
  return (
    <td class={CELL[size]}>
      <div class={`flex items-center justify-center ${LINE[size]}`}>
        <Flag {...flag} />
      </div>
    </td>
  );
}

/** One field's row: key, label, type and its settings toggle, required, many, shown on the table (not link fields), and
 *  order/remove. */
function FieldRow({ row: r, env, parent, toggle, onChange, onRemove, onMove }: RowProps & { toggle: Toggle }) {
  const set = (patch: Partial<Row>) => onChange({ ...r, ...patch });
  const link = parent !== undefined;
  const name = r.key || t("fields.unnamed");
  const size: Size = link ? "compact" : "normal";
  return (
    <tr class={link ? "bg-zinc-50" : undefined}>
      <KeyCell r={r} parent={parent} set={set} env={env} />
      <td class={CELL[size]}>
        <Input
          aria-label={t("fields.label")}
          compact={link}
          class="min-w-32"
          value={r.label ?? ""}
          onValue={(v) => set({ label: v })}
        />
      </td>
      <TypeCell r={r} env={env} link={link} name={name} toggle={toggle} set={set} />
      <FlagCell
        size={size}
        text={t("fields.requiredLetter")}
        title={t("fields.required")}
        label={t("fields.requiredOf", { field: name })}
        checked={r.required ?? false}
        onCheck={(v) => set({ required: v })}
      />
      <FlagCell
        size={size}
        text={t("fields.manyLetter")}
        title={t("fields.manyHint")}
        label={t("fields.manyOf", { field: name })}
        checked={r.many ?? false}
        onCheck={(v) => set({ many: v })}
      />
      {link ? (
        <td class={CELL[size]} />
      ) : (
        <FlagCell
          size={size}
          text={t("fields.tableLetter")}
          title={t("fields.tableHint")}
          label={t("fields.tableOf", { field: name })}
          checked={r.show_on_table ?? false}
          onCheck={(v) => set({ show_on_table: v })}
        />
      )}
      <td class={`${CELL[size]} text-right whitespace-nowrap`}>
        <Button size="sm" variant="ghost" aria-label={t("fields.moveUp", { key: name })} onClick={() => onMove(-1)}>
          <ArrowUpIcon />
        </Button>
        <Button size="sm" variant="ghost" aria-label={t("fields.moveDown", { key: name })} onClick={() => onMove(1)}>
          <ArrowDownIcon />
        </Button>
        <Button size="sm" variant="ghost" aria-label={t("fields.removeField", { key: name })} onClick={onRemove}>
          <CloseIcon />
        </Button>
      </td>
    </tr>
  );
}

/**
 * A field's settings on a tinted line, lined up with the (indented) link fields' keys. The section under a field
 * has 12px at its edges and 8px between lines; a ref field's line is ruled off from its link fields', 12px each side.
 */
function SettingsLine(p: {
  r: Row;
  env: Env;
  link: boolean;
  set: (patch: Partial<Row>) => void;
  id?: string;
  hidden?: boolean;
}) {
  const ruled = !p.link && p.r.type === "ref";
  return (
    <tr id={p.id} hidden={p.hidden} class="bg-zinc-50">
      <td colSpan={COLUMNS} class={p.link ? "py-1 pr-2 pl-6" : `pr-2 pl-6 pt-3 ${ruled ? "pb-2" : "pb-3"}`}>
        <div class="flex flex-wrap items-start gap-x-4 gap-y-1">
          <Settings r={p.r} env={p.env} link={p.link} set={p.set} />
        </div>
        {/* 12px each side: this line's 8px bottom padding and the next row's 4px top make the lower 12. */}
        {ruled ? <hr class="mt-3 border-zinc-200" /> : null}
      </td>
    </tr>
  );
}

/** A link field: its row, then its own settings line, shown and hidden with its own toggle. */
function LinkField(p: RowProps & { parent: Row }) {
  const toggle = useToggle(p.row, p.env, true);
  return (
    <>
      <FieldRow {...p} toggle={toggle} />
      {toggle.has ? (
        <SettingsLine
          id={toggle.id}
          hidden={!toggle.shown}
          r={p.row}
          env={p.env}
          link={true}
          set={(patch) => p.onChange({ ...p.row, ...patch })}
        />
      ) : null}
    </>
  );
}

/** `rows` with the one at `i` moved by `by` places (clamped). */
function moved<T>(rows: readonly T[], i: number, by: number): T[] {
  const next = [...rows];
  const [r] = next.splice(i, 1);
  if (r !== undefined) {
    next.splice(Math.max(0, Math.min(next.length, i + by)), 0, r);
  }
  return next;
}

/** A ref field's link fields, and a row to add one. */
function LinkFields({ r, env, onChange }: { r: Row; env: Env; onChange: (r: Row) => void }) {
  const links = r.fields ?? [];
  const setLinks = (fields: Row[]) => onChange({ ...r, fields });
  return (
    <>
      {links.map((l, i) => (
        <LinkField
          key={l.uid}
          row={l}
          env={env}
          parent={r}
          onChange={(next) => setLinks(links.map((x) => (x.uid === l.uid ? next : x)))}
          onRemove={() => setLinks(links.filter((x) => x.uid !== l.uid))}
          onMove={(by) => setLinks(moved(links, i, by))}
        />
      ))}
      <tr class="bg-zinc-50">
        <td colSpan={COLUMNS} class="pt-1 pr-2 pb-3 pl-6">
          <div class="flex items-center gap-3">
            <Button size="xs" title={t("fields.linkFieldsHint")} onClick={() => setLinks([...links, blank()])}>
              {t("fields.addLinkField")}
            </Button>
            {links.length === 0 ? <span class="text-xs text-zinc-500">{t("fields.linkFieldsHint")}</span> : null}
          </div>
        </td>
      </tr>
    </>
  );
}

/**
 * A field: its row in one table body, then in a second one, shown and hidden with the field's toggle, its settings
 * and (a ref field's) link fields.
 */
function FieldGroup(p: RowProps) {
  const r = p.row;
  const toggle = useToggle(r, p.env, false);
  return (
    <>
      <tbody class="border-t border-zinc-200">
        <FieldRow {...p} toggle={toggle} />
      </tbody>
      {toggle.has ? (
        <tbody id={toggle.id} hidden={!toggle.shown}>
          <SettingsLine r={r} env={p.env} link={false} set={(patch) => p.onChange({ ...r, ...patch })} />
          {r.type === "ref" ? <LinkFields r={r} env={p.env} onChange={p.onChange} /> : null}
        </tbody>
      ) : null}
    </>
  );
}

/** The fields of an entity as a table; each field's settings and link fields go on rows under it. */
export function FieldList({ rows, setRows, env }: { rows: Row[]; setRows: (r: Row[]) => void; env: Env }) {
  const limitId = useId();
  return (
    <>
      <h2 class="mt-2 text-sm font-semibold text-zinc-700">{t("entities.fields")}</h2>
      {rows.length > 0 ? (
        <div class="relative overflow-x-auto rounded-lg border border-zinc-200 bg-white">
          <table class="min-w-full text-sm">
            <thead class="bg-zinc-50">
              <tr>
                <th scope="col" class={TH}>
                  {t("fields.key")}
                </th>
                <th scope="col" class={TH}>
                  {t("fields.label")}
                </th>
                <th scope="col" class={TH}>
                  {t("fields.type")}
                </th>
                <th scope="col" class={FLAG_TH}>
                  {t("fields.required")}
                </th>
                <th scope="col" class={FLAG_TH} title={t("fields.manyHint")}>
                  <span class="cursor-help underline decoration-zinc-400 decoration-dotted underline-offset-4">
                    {t("fields.manyShort")}
                  </span>
                </th>
                <th scope="col" class={FLAG_TH} title={t("fields.tableHint")}>
                  <span class="cursor-help underline decoration-zinc-400 decoration-dotted underline-offset-4">
                    {t("fields.tableShort")}
                  </span>
                </th>
                <th scope="col">
                  <span class="sr-only">{t("fields.actions")}</span>
                </th>
              </tr>
            </thead>
            {rows.map((r, i) => (
              <FieldGroup
                key={r.uid}
                row={r}
                env={env}
                onChange={(next) => setRows(rows.map((x) => (x.uid === r.uid ? next : x)))}
                onRemove={() => setRows(rows.filter((x) => x.uid !== r.uid))}
                onMove={(by) => setRows(moved(rows, i, by))}
              />
            ))}
          </table>
        </div>
      ) : null}
      {rows.length < MAX_FIELDS ? (
        <Button class="self-start" onClick={() => setRows([...rows, blank()])}>
          {t("entities.addField")}
        </Button>
      ) : (
        <span class="flex items-center gap-3 self-start">
          <Button disabled={true} aria-describedby={limitId}>
            {t("entities.addField")}
          </Button>
          <span id={limitId} class="text-xs text-zinc-500">
            {t("entities.fieldLimitReached", { limit: MAX_FIELDS })}
          </span>
        </span>
      )}
    </>
  );
}
